import * as T from 'three';
import { TextBox, FileUploader, Select, Checkbox, Button, Slider, Container, TextInput } from '../ui/pageElements';
import { AnimationInterface } from './animationInterface';
import { JointInterface } from './jointInterface';
import { recurseMaterialTraverse } from '../robotFunctions/robotHelpers';
import { DataInterface } from './dataInterface';
import { getJointHeirarchy } from '../robotFunctions/jointHeirarchy';

const LIST_CONTAINER = "robotListContainer";

export class RobotGroup {
    constructor(robot, interfaces, url = null) {
        this.robot = robot;
        this.parent = new T.Group();

        this.rotationParent = new T.Group();
        this.parent.add(this.rotationParent);
        this.rotationParent.add(this.robot);
        interfaces.scene.add(this.parent)

        this.parent.position.y = 1
        this.robot.rotation.x = -Math.PI / 2;
        // interfaces.scene.add(this.robot);
        this.robotName = robot.robotName
        this.animationInterface;
        this.interfaces = interfaces;
        this.endEffectorSelector;
        this.url = url;
        this.meshes = [];
        this.highlighted = false;
        this.endEffector;
        this.animationInterface = new AnimationInterface(this, this.scene, this.interfaces);
        this.jointInterface = new JointInterface(robot, this.parent, this.interfaces);
        this.dataInterface = new DataInterface(this)
        this.endEffectorPosition = new T.Vector3();
        this.endEffectorRotation = new T.Quaternion();

        this.container = new Container("robotContainer", LIST_CONTAINER);
        this.robotLabel = new TextBox(this.robot.robotName + "label", LIST_CONTAINER, this.robot.robotName)

        // show robot check
        this.visibilityCheck = new Checkbox("Show Robot", LIST_CONTAINER, true);
        this.visibilityCheck.setToolTip(this.interfaces.tooltips.showRobot, "leftToolTip");
        this.visibilityCheck.checkbox.onchange = () => {
            this.robot.visible = this.visibilityCheck.checkbox.checked;
            this.endEffectorObject.visible = this.robot.visible && this.endEffectorCheck.checkbox.checked;
        }

        // show end effector check
        this.endEffectorCheck = new Checkbox("Show End Effector", LIST_CONTAINER, true);
        this.endEffectorCheck.setToolTip(this.interfaces.tooltips.showEndEffector, "leftToolTip");
        this.endEffectorCheck.checkbox.onchange = () => {
            this.endEffectorObject.visible = this.robot.visible && this.endEffectorCheck.checkbox.checked;
            this.robot.traverse(c => {
                if (c.material) {
                    recurseMaterialTraverse(c.material, (material) => {
                        material.opacity = 1;
                    })
                }
            })
        }

        // hoverable check
        this.hoverableCheck = new Checkbox("Hoverable", LIST_CONTAINER, true);
        this.hoverableCheck.setToolTip(this.interfaces.tooltips.hoverable, "leftToolTip");

        // opacity slider
        this.opacitySlider = new Slider("Opacity", LIST_CONTAINER, 0, 1, 0.01, 1);
        this.opacitySlider.setToolTip(this.interfaces.tooltips.robotOpacity, "leftToolTip");
        this.opacitySlider.label.innerHTML = "Opacity: 1";
        this.opacitySlider.slider.oninput = () => {
            this.opacitySlider.label.innerHTML = "Robot Opacity: " + String(this.opacitySlider.slider.value);
            this.robot.traverse(c => {
                if (c.material) {
                    recurseMaterialTraverse(c.material, (material) => {
                        // material.tranparent = true;
                        // material.needsUpdate = true;
                        material.opacity = Number(this.opacitySlider.slider.value);
                    })
                }
            })
        }

        // end effector selector
        this.endEffectorsList = Object.entries(this.robot.joints).concat(Object.entries(this.robot.links));
        this.endEffectorSelector = new Select("End Effector Selector", LIST_CONTAINER, this.endEffectorsList);
        this.endEffectorSelector.setToolTip(this.interfaces.tooltips.endEffectorSelector, "leftToolTip");
        this.endEffectorSelector.select.onchange = () => {
            this.endEffector = this.endEffectorSelector.getSelected();
        }
        this.endEffector = this.endEffectorSelector.getSelected();

        this.robot.traverse(c => {
            if (c.type == "Mesh") {
                this.meshes.push(c);
            }
        });

        // end effector
        let geom = new T.ConeGeometry(0.01, 0.03, 20);
        let mat = new T.MeshStandardMaterial({
            color: "orange"
        });
        this.endEffectorObject = new T.Mesh(geom, mat);
        this.endEffectorObject.visible = true;
        this.interfaces.scene.add(this.endEffectorObject);

        this.container.addBlock(this.robotLabel, this.visibilityCheck, this.endEffectorCheck, this.hoverableCheck, this.opacitySlider, this.endEffectorSelector);
        this.container.div.onmousedown = () => {
            this.interfaces.robot.setRobot(this.robot.robotName);
        }

        console.log(this.robot)

        this.jointCones = []
        // this.jointInterface.getJoints().forEach(joint => {
        //     console.log(joint[1])
        //     console.log(joint[1].axis)
        // })
        
        this.jointHeirarchy = getJointHeirarchy(this.robot)
    }

    // updates the cones for the joints and end effector
    updateEndEffector() {
        let focusPos = this.endEffector.getWorldPosition(this.endEffectorPosition);
        let focusRotation = this.endEffector.getWorldQuaternion(this.endEffectorRotation);

        this.endEffectorObject.position.set(focusPos.x, focusPos.y, focusPos.z);
        this.endEffectorObject.quaternion.copy(focusRotation);

        this.dataInterface.update()
    }

    getEndEffector() {
        return this.endEffector;
    }

    getJointConfigs() {
        return this.jointInterface.getJointConfigs();
    }

    unmarkDiv() {
        this.container.div.classList.remove("selectedRobot")
    }

    markDiv() {
        this.container.div.classList.add("selectedRobot")
    }

    highlightRobot(highlight) {
        if(this.highlighted != highlight) {
            this.robot.traverse(c => {
                if (c.material) {
                    recurseMaterialTraverse(c.material, (material) => {
                        if(highlight) {
                            material.color = material.highlightColor.clone();
                        } else {
                            if(this.interfaces.robot.getSelectedRobot() == this.robot) {
                                material.color = material.baseColor;
                            } else {
                                material.color = material.desaturatedColor.clone();
                            }
                        }
                    })
                }
            });
            this.highlighted = highlight;
        }
    }
    
    updateColors() {
        this.robot.traverse(c => {
            if (c.material) {
                recurseMaterialTraverse(c.material, (material) => {
                    if(this.interfaces.robot.getSelectedRobot() == this.robot) {
                        material.color = material.baseColor;
                    } else {
                        material.color = material.desaturatedColor.clone();
                    }
                })
            }
        });
    }

    getSettings() {
        return {
            robotVisible: this.visibilityCheck.checkbox.checked,
            eeVisible: this.endEffectorCheck.checkbox.checked,
            opacity: this.opacitySlider.slider.value,
            hoverable: this.hoverableCheck.checkbox.checked,
            endEffector: this.endEffectorSelector.getSelectedName(),
            parallelMotions: this.animationInterface.getParallelMotionNames(),
            isParallelMotionShown: this.animationInterface.isParallelMotionShown
        }
    }

    setSettings(settings) {
        // robot visibility
        this.visibilityCheck.checkbox.checked = (settings.robotVisible === 'true');
        this.robot.visible = this.visibilityCheck.checkbox.checked;

        // end effector
        this.endEffectorSelector.setSelected(settings.endEffector);
        this.endEffector = this.endEffectorSelector.getSelected();

        // end effector visibility
        this.endEffectorCheck.checkbox.checked = (settings.eeVisible === 'true')
        this.endEffectorObject.visible = this.robot.visible && this.endEffectorCheck.checkbox.checked;

        //hoverable
        this.hoverableCheck.checkbox.checked = (settings.hoverable === 'true')

        // opacity
        this.opacitySlider.slider.value = settings.opacity;
        this.opacitySlider.label.innerHTML = "Robot Opacity: " + String(this.opacitySlider.slider.value);
        this.robot.traverse(c => {
            if (c.material) {
                recurseMaterialTraverse(c.material, (material) => {
                    material.opacity = Number(this.opacitySlider.slider.value);
                })
            }
        })

        // parallel motions
        let parallelMotions = settings.parallelMotions;
        // console.log(this.robotName, parallelMotions)
        for(let i = 0; i < parallelMotions.length; i += 3) {
            if(parallelMotions[i+1]) {
                this.animationInterface.addParallelMotion(parallelMotions[i], parallelMotions[i+1], parallelMotions[i+2])
            }
        }

        let isParallelMotionShown = settings.isParallelMotionShown;
        this.animationInterface.toggleShowParallelMotions(isParallelMotionShown)
    }
}